import { lazy, Suspense, useMemo } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router';
import { useAuth } from '@/shared/lib/auth-context';
import RouteErrorBoundary from '@/app/ErrorBoundary';
import AppShell from '@/app/AppShell';
import LoginPage from '@/app/login/page';
import RecsysPage from '@/app/recsys/page';
import { Skeleton } from '@/components/ui/skeleton';

const DashboardPage = lazy(() => import('@/app/dashboard/page'));
const DesignerDetailPage = lazy(() => import('@/app/dashboard/DesignerDetailPage'));
const KitchenSinkPage = lazy(() => import('@/app/kitchen-sink/page'));

function PageFallback() {
    return (
        <div className="flex h-full w-full items-center justify-center p-8">
            <Skeleton className="h-10 w-48" />
        </div>
    );
}

function LazyPage({ children }: { children: React.ReactNode }) {
    return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

function RequireAuth() {
    const auth = useAuth();
    const location = useLocation();
    if (!auth.isAuthenticated) {
        const token = new URLSearchParams(location.search).get('token');
        if (token) sessionStorage.setItem('launch_token', token);
        return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return <Outlet />;
}

function HomeRedirect() {
    const auth = useAuth();
    return <Navigate to={auth.isAuthenticated ? '/recsys' : '/login'} replace />;
}

function LoginRoute() {
    const auth = useAuth();
    if (auth.user?.is_guest) return <Navigate to="/recsys" replace />;
    return <LoginPage />;
}

export default function AppRouter() {
    const router = useMemo(() => createBrowserRouter([
        {
            path: '/',
            element: <Outlet />,
            errorElement: <RouteErrorBoundary />,
            children: [
                {
                    path: 'login',
                    element: <LoginRoute />,
                },
                {
                    element: <AppShell />,
                    children: [
                        { index: true, element: <HomeRedirect /> },
                        {
                            element: <RequireAuth />,
                            children: [
                                {
                                    path: 'recsys',
                                    element: <RecsysPage />,
                                    errorElement: <RouteErrorBoundary />,
                                },
                                {
                                    path: 'dashboard',
                                    element: (
                                        <LazyPage>
                                            <DashboardPage />
                                        </LazyPage>
                                    ),
                                    errorElement: <RouteErrorBoundary />,
                                },
                                {
                                    path: 'dashboard/designer/:designerEmail',
                                    element: (
                                        <LazyPage>
                                            <DesignerDetailPage />
                                        </LazyPage>
                                    ),
                                    errorElement: <RouteErrorBoundary />,
                                },
                                {
                                    path: 'kitchen-sink',
                                    element: (
                                        <LazyPage>
                                            <KitchenSinkPage />
                                        </LazyPage>
                                    ),
                                },
                            ],
                        },
                        { path: '*', element: <Navigate to="/" replace /> },
                    ],
                },
            ],
        },
    ], { basename: import.meta.env.VITE_BASE_URL }), []);
    return <RouterProvider router={router} />;
}
